import { useEffect, useState } from 'react';
import axiosInstance from '../../../main/initializers/axios';
import { toast } from 'react-toastify';

import eFormType from '../../../main/assets/enums/eFormType';
import IPrinter from '../../../main/interfaces/IPrinter';
import { IPrinterTemplate } from '../../../main/interfaces/IPrinterTemplate';

import PrinterTemplate from '../printerTemplate/PrinterTemplate';

import '../Printers.css';
import useGetEnums from '../../../main/hooks/useGetEnums';
import GeneralSelect from '../../../main/components/select/GeneralSelect';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
interface Props {
  tempPrinter?: IPrinter;
  modalType: eFormType;
  createOrUpdate: boolean;
  onSubmitData: () => void;
}
type TempPrinterTemplate = IPrinterTemplate & { tempId?: number };
const DEFAULT_PRINTING_OPERATION = 1;

const normalizeTemplateOperation = (template: TempPrinterTemplate): TempPrinterTemplate => ({
  ...template,
  operation: template.operation && template.operation > 0 ? template.operation : DEFAULT_PRINTING_OPERATION,
});

const normalizePrinter = (value?: IPrinter): IPrinter => ({
  ...(value ?? {}),
  isActive: value?.isActive ?? true,
  operation: value?.operation && value.operation > 0 ? value.operation : DEFAULT_PRINTING_OPERATION,
  templates: (value?.templates ?? []).map((tpl: TempPrinterTemplate) => normalizeTemplateOperation(tpl)),
});

const PrinterEdit = ({ tempPrinter, modalType, createOrUpdate, onSubmitData }: Props) => {
  const [printer, setPrinter] = useState<IPrinter>(normalizePrinter({
    isActive: true,
    templates: [],
  }));

  const { i18n } = useTranslation();
  const allEnums = useGetEnums();
  const [newTemplate, setNewTemplate] = useState<IPrinterTemplate | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const {
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      operation: printer.operation ?? DEFAULT_PRINTING_OPERATION,
    },
  });

  useEffect(() => {
    if (modalType === eFormType.Edit && tempPrinter) {
      setPrinter(normalizePrinter(tempPrinter));
    }
  }, [tempPrinter, modalType]);

  useEffect(() => {
    setValue('operation', printer.operation ?? DEFAULT_PRINTING_OPERATION);
  }, [printer.operation, setValue]);

  useEffect(() => {
    if (!createOrUpdate || isSaving) return;
    savePrinter();
  }, [createOrUpdate]);

  const savePrinter = async () => {
    setIsSaving(true);
    try {
      const printerToSave: IPrinter = normalizePrinter(printer);
      const res = await axiosInstance.post('/api/utility/printers/save', printerToSave);
      setPrinter(normalizePrinter(res.data));
      toast.success(i18n.t('printer.printerSavedSuccessfully'));
      onSubmitData();
    } catch (error) {
      toast.error(i18n.t('printers.errorSavingPrinter'));
      console.error('Save printer error:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const handleAddTemplateClick = () => {
    const newTpl: TempPrinterTemplate = {
      printerId: printer.id,
      companyId: printer.companyId,
      operation: printer.operation || DEFAULT_PRINTING_OPERATION,
      name: '',
      content: '',
      isActive: true,
      tempId: Date.now(),
    };
    setPrinter((prev) => ({
      ...prev,
      templates: [...(prev.templates ?? []), newTpl],
    }));
  };
  const handleTemplateChange = (updatedTemplate: TempPrinterTemplate) => {
    const normalizedTemplate = normalizeTemplateOperation(updatedTemplate);
    setPrinter((prev) => ({
      ...prev,
      templates: prev.templates?.map((tpl: TempPrinterTemplate) => {
        if (normalizedTemplate.id && tpl.id === normalizedTemplate.id) {
          return normalizedTemplate;
        }
        if (!normalizedTemplate.id && tpl.tempId === normalizedTemplate.tempId) {
          return normalizedTemplate;
        }

        return tpl;
      }),
    }));
  };
  return (
    <div className="printer-form">
      {isSaving && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>{i18n.t('company.saving')}</p>
        </div>
      )}
      <div className="form-row-single">
        <div className="form-field">
          <label className="form-label">{i18n.t('printers.printerName')}</label>
          <input
            type="text"
            className="form-input"
            value={printer.printerName ?? ''}
            onChange={(e) => setPrinter({ ...printer, printerName: e.target.value })}
            placeholder={i18n.t('printers.enterPrinterName')}
          />
        </div>
        <div className="form-field">
          <GeneralSelect
            label={i18n.t('entity.Operation')}
            name="operation"
            data={allEnums.PrintingOperation}
            value="name"
            translationTag="enum.printingOperation"
            required
            control={control}
            errors={errors}
            setSelectedVal={(val) => {
              setPrinter((prev) => ({
                ...prev,
                operation: Number(val),
              }));
            }}
          />
        </div>
        <div className="form-field">
          <label className="form-label checkbox-label">
            <input
              type="checkbox"
              className="checkbox-input"
              checked={printer.isActive ?? false}
              onChange={(e) =>
                setPrinter({
                  ...printer,
                  isActive: e.target.checked,
                })
              }
            />
            <span className="checkbox-text">{i18n.t('entity.Active')}</span>
          </label>
        </div>
      </div>
      {/* Templates Section */}
      {modalType === eFormType.Edit && (
        <div className="templates-section">
          <div className="templates-header">
            <h4 className="templates-title">{i18n.t('printers.templates')}</h4>
            <button type="button" className={`btn ${showTemplateForm ? 'btn-cancel' : 'btn-add'}`} onClick={handleAddTemplateClick}>
              + {i18n.t('printers.addTemplates')}
            </button>
          </div>
          <div className="templates-list">
            {printer.templates && printer.templates.length > 0 ? (
              printer.templates.map((tpl: TempPrinterTemplate) => (
                <div key={tpl.id || tpl.tempId} className="template-card">
                  <PrinterTemplate template={tpl} onChange={handleTemplateChange} />
                </div>
              ))
            ) : (
              <div className="empty-state">
                <p className="empty-state-text">{i18n.t('printers.noTemplatesAddedYet')}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PrinterEdit;
